// Import from NPM
// -------------------------------------
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader, Image, Button, Divider } from "semantic-ui-react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useFeatures } from "flagged";
import localforage from "localforage";
import CryptoJS from "crypto-js";
import _ from "lodash";

// Import from Config
// -------------------------------------
import { login } from "@reducers/authSlice";
import { apiV6, baseUrl } from "@api/apiV6";
import { Div } from "@components/Generics.react";
import { settings } from "@config/settings/app.settings";
import { devMode } from "@utilities/helpers";
import AuthLayout from "@layouts/AuthLayout.react";
import { logIt } from "@utilities/logHelpers";
import { useNavigate, useLocation } from "react-router-dom";

// Import Components
// -------------------------------------

export default function AutoLogin({ team, token, setToken, branding, authPageLinks }) {
    const dispatch = useDispatch();
    const features = useFeatures();
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(true);

    const wPageHt = window.innerHeight - 100;
    const isPortrait = window.innerHeight > window.innerWidth;

    // Handle token from URL or storage
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlToken = params.get("token");
        const userStatus = params.get("userStatus");

        if (urlToken) {
            localStorage.setItem("token", urlToken);
            if (userStatus === "new") {
                navigate("/builder/registered", { 
                    state: { fromAuth: true }, 
                    replace: true 
                });
                return;
            }
            setToken(urlToken);
        } else {
            const savedToken = localStorage.getItem("token");
            savedToken ? setToken(savedToken) : navigate("/login", { replace: true });
        }
    }, [location.search, navigate, setToken]);

    // Authenticate user with token
    useEffect(() => {
        if (!token) return;
        const params = new URLSearchParams(location.search);
        if (params.get("userStatus") === "new") return;

        const authenticateUser = async () => {
            setIsProcessing(true);
            try {
                //const authEndpoint = features.auth.signInOrUp ? "connect" : "sign_in";
                const response = await axios.post(
                    `${baseUrl}api/v3/auth/connect`, 
                    {}, 
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                // --- 🔐 Decrypt if encrypted (from version 1)
                let payload;
                try {
                    if (typeof response.data === "string") {
                        // Try to decrypt
                        const decrypted = CryptoJS.AES.decrypt(response.data, "16t17t5m").toString(CryptoJS.enc.Utf8);
                        payload = JSON.parse(decrypted);
                    } else {
                        payload = response.data;
                    }
                } catch (e) {
                    // fallback to plain JSON parse
                    payload = typeof response.data === "string" 
                        ? JSON.parse(response.data) 
                        : response.data;
                }

                if (!payload || payload.error) throw new Error(payload?.message || "Invalid token");

                // --- 🎨 Adjust Branding like in version 1
                let brandingAdjustedUser = _.cloneDeep(payload);
                brandingAdjustedUser.branding = brandingAdjustedUser.branding || {};
                brandingAdjustedUser.branding.colors = brandingAdjustedUser.branding.colors || {
                    main: settings.colors.main,
                    primary: settings.colors.primary,
                    primaryText: settings.colors.primaryText,
                };
                if (team != null) window.localStorage.setItem("team", payload.team);

                // --- ✅ Dispatch login
                await dispatch(apiV6.util.resetApiState());
                await dispatch(login({ 
                    user: brandingAdjustedUser, 
                    token: response.headers["access-token"] 
                }));
                await dispatch(apiV6.endpoints.getArticles.initiate());
                await dispatch(apiV6.endpoints.getCourseLibrary.initiate());

                // --- 🔀 Handle redirectPath
                let redirectPath = localStorage.getItem("redirectPath");
                if (redirectPath) {
                    redirectPath = redirectPath !== "/" && redirectPath !== "/logout"
                        ? `${devMode ? "" : settings.targetRoute.slice(0, -1)}${redirectPath}`
                        : "/";
                }
                const redirectUrl = redirectPath || "/";
                localStorage.removeItem("redirectPath");

                navigate(redirectUrl, { replace: true });
            } catch (err) {
                setError(err.response?.data?.message || err.message || "Authentication failed");
                localStorage.removeItem("token");
                logIt({
                    page: "Login",
                    problem: `Token Login failed with ${token}`,
                    criticality: 0,
                    trace: err.response?.data?.message,
                });
            } finally {
                setIsProcessing(false);
            }
        };

        authenticateUser();
    }, [token, location.search, navigate, dispatch, features, team]);

    // Clear cache
    const clearCache = () => {
        localStorage.clear();
        sessionStorage.clear();
        caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
        localforage.clear();
        window.location.reload();
    };

    return (
        <AuthLayout title={`Login to ${t("appName")}`} team={team} teamLogin={true}>
            <Div fluid superpad ht={isPortrait ? wPageHt : "100%"} flex centered maxWd="480px">
                <Div wd="60%" gutter>
                    <Image src={branding?.image} />
                </Div>
                <Div fluid white fullHt superpad rounded>
                    {error ? (
                        <Div center-align large>
                            <Div gutter>{error}</Div>
                            <Button negative onClick={clearCache} content={t("auth.loginPage.retry")} />
                        </Div>
                    ) : (
                        <Loader active>{t("auth.loginPage.loggingIn")}</Loader>
                    )}
                    <Divider />
                    <Div txtMain center-align clickable onClick={() => setToken(null)}>
                        {t(`auth.authLinks.upass`)}
                    </Div>
                    {authPageLinks}
                </Div>
            </Div>
        </AuthLayout>
    );
}
